import { ArrowRight } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const Hero = () => {
  const textRef = useScrollReveal();
  const illustrationRef = useScrollReveal(250);

  return (
    <section className="relative overflow-hidden bg-background py-20 md:py-32">
      <div className="mx-auto flex max-w-[1320px] flex-col items-center gap-12 px-6 md:flex-row md:gap-16">
        {/* Text */}
        <div ref={textRef} className="flex max-w-xl flex-col items-start reveal-target" data-reveal>
          <h1
            className="text-4xl font-extrabold leading-[1.08] tracking-tight text-foreground md:text-[3.25rem]"
            style={{ textWrap: "balance" }}
          >
            Reliable IT Support, Software Development, and Automation Solutions
          </h1>
          <p
            className="mt-6 text-lg leading-relaxed text-muted-foreground"
            style={{ textWrap: "pretty" }}
          >
            Helping businesses build, maintain, and automate the technology they
            rely on every day.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href="#contact"
              className="inline-flex h-12 items-center gap-2 rounded-btn bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-sm transition-colors duration-150 hover:bg-primary/85 active:scale-[0.97]"
            >
              Get Started
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#services"
              className="inline-flex h-12 items-center rounded-btn border border-border px-6 text-sm font-semibold text-foreground transition-colors duration-150 hover:bg-muted active:scale-[0.97]"
            >
              Our Services
            </a>
          </div>
        </div>

        {/* Abstract Tech Illustration */}
        <div ref={illustrationRef} className="relative flex w-full max-w-lg flex-shrink-0 items-center justify-center" data-reveal="right">
          <HeroIllustration />
        </div>
      </div>
    </section>
  );
};

const HeroIllustration = () => (
  <div className="relative h-[340px] w-full md:h-[400px]">
    {/* Grid lines */}
    <svg
      className="absolute inset-0 h-full w-full text-border"
      viewBox="0 0 400 400"
      fill="none"
    >
      {[80, 160, 240, 320].map((x) => (
        <line key={`v${x}`} x1={x} y1={0} x2={x} y2={400} stroke="currentColor" strokeWidth="0.5" />
      ))}
      {[80, 160, 240, 320].map((y) => (
        <line key={`h${y}`} x1={0} y1={y} x2={400} y2={y} stroke="currentColor" strokeWidth="0.5" />
      ))}
    </svg>

    {/* Dashboard card */}
    <div className="absolute left-[10%] top-[12%] w-[55%] rounded-card border border-border bg-card p-4 shadow-lg">
      <div className="mb-3 flex items-center gap-2">
        <div className="h-2 w-2 rounded-full bg-green-400" />
        <span className="text-[11px] font-medium text-muted-foreground">System Status</span>
      </div>
      <div className="space-y-2">
        <div className="h-2 w-full rounded-full bg-muted">
          <div className="h-2 w-[82%] rounded-full bg-accent" />
        </div>
        <div className="h-2 w-full rounded-full bg-muted">
          <div className="h-2 w-[65%] rounded-full bg-accent/70" />
        </div>
        <div className="h-2 w-full rounded-full bg-muted">
          <div className="h-2 w-[91%] rounded-full bg-accent/50" />
        </div>
      </div>
    </div>

    {/* Metrics card */}
    <div className="absolute bottom-[15%] right-[8%] w-[48%] rounded-card border border-border bg-card p-4 shadow-lg">
      <span className="text-[11px] font-medium text-muted-foreground">Uptime</span>
      <p className="mt-1 text-2xl font-bold tracking-tight text-foreground tabular-nums">99.97%</p>
      <div className="mt-2 flex items-end gap-[3px]">
        {[40, 55, 38, 62, 70, 58, 75, 80, 72, 85, 78, 90].map((h, i) => (
          <div
            key={i}
            className="w-[6px] rounded-sm bg-accent/40"
            style={{ height: `${h * 0.4}px` }}
          />
        ))}
      </div>
    </div>

    {/* Floating node */}
    <div className="absolute right-[15%] top-[18%] flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card shadow-md">
      <div className="h-3 w-3 rounded-full bg-accent" />
    </div>

    {/* Connection lines */}
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 400 400" fill="none">
      <path d="M220 100 L320 80" stroke="hsl(207 54% 40% / 0.3)" strokeWidth="1" strokeDasharray="4 4" />
      <path d="M200 200 L280 280" stroke="hsl(207 54% 40% / 0.2)" strokeWidth="1" strokeDasharray="4 4" />
    </svg>
  </div>
);

export default Hero;
